import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";
import useClientMetadataSearch from "../../Hooks/useClientMetadataSearch";
import AutoCompletableInput from "../AutoCompletableInputComponent/AutoCompletableInput";

const ClientMetadataLookupInput = ({
    value,
    handleValueChange,
    noValidate = true,
    isValueValid,
    handleClientMetadataRecordSelection,
    inputLabel,
    placeholder = "",
    customErrorMessage = "",
    disabled = false
}) => {
    const {
        clientMetadataSearchResults,
        loadingClientMetadata,
        lookupClientMetadata
    } = useClientMetadataSearch();

    // Transform search results to options format
    const options = useMemo(() => {
        if (!clientMetadataSearchResults || !Array.isArray(clientMetadataSearchResults)) {
            return [];
        }
        return clientMetadataSearchResults.map(result => ({
            value: result.companyName, // Use companyName as the value
            label: result.companyName,
            data: result
        }));
    }, [clientMetadataSearchResults]);

    // Handle input value changes
    const handleChange = useCallback((inputValue) => {
        handleValueChange(inputValue);
    }, [handleValueChange]);

    // Handle search triggering
    const handleSearch = useCallback(async (inputValue) => {
        if (inputValue && inputValue.length >= 2) {
            try {
                await lookupClientMetadata(inputValue);
            } catch (error) {
                console.error('Error searching client metadata:', error);
            }
        }
    }, [lookupClientMetadata]);

    // Handle option selection
    const handleSelect = useCallback((selectedData) => {
        // Update the input value with the selected company name
        handleValueChange(selectedData.data.companyName);
        
        // Call the metadata selection handler if provided
        if (handleClientMetadataRecordSelection) {
            handleClientMetadataRecordSelection(selectedData);
        }
    }, [handleValueChange, handleClientMetadataRecordSelection]);

    return (
        <div className='client-metadata-lookup-input'>
            <AutoCompletableInput
                value={value}
                onChange={handleChange}
                options={options}
                onInputChange={handleSearch}
                isLoading={loadingClientMetadata}
                placeholder={placeholder}
                label={inputLabel}
                isInvalid={!noValidate && !isValueValid}
                errorMsg={customErrorMessage}
                disabled={disabled}
                displayKey="label"
                valueKey="value"
                onSelect={handleSelect}
                minimumInputLength={2}
            />
        </div>
    );
}

ClientMetadataLookupInput.propTypes = {
    value: PropTypes.string.isRequired,
    handleValueChange: PropTypes.func.isRequired,
    inputLabel: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    noValidate: PropTypes.bool,
    isValueValid: PropTypes.bool,
    handleClientMetadataRecordSelection: PropTypes.func,
    customErrorMessage: PropTypes.string,
    disabled: PropTypes.bool
};

export default ClientMetadataLookupInput;